import {RefObject, useCallback, useState} from "react";
import {ActionMeta, GroupBase, InputActionMeta, OnChangeValue, SelectInstance} from "react-select";

import {SelectProps} from "@/components/select/Select.types.ts";

type MultiSelectSearchPersistenceArgs<Option, IsMulti extends boolean, Group extends GroupBase<Option>> = Pick<
  SelectProps<Option, IsMulti, Group>,
  "isMulti" | "inputValue" | "onInputChange" | "onChange" | "onCreateOption"
> & {
  selectRef: RefObject<SelectInstance<Option, IsMulti, Group> | null>;
};

// Keeps the typed search text in multi-selects after picking an option, so users can keep filtering without retyping.
// Creating a new option clears it instead — the text became a value, so keeping it would filter the menu to nothing.
export function useMultiSelectSearchPersistence<Option, IsMulti extends boolean, Group extends GroupBase<Option>>({
  isMulti,
  selectRef,
  inputValue,
  onInputChange,
  onChange,
  onCreateOption,
}: MultiSelectSearchPersistenceArgs<Option, IsMulti, Group>) {
  const [internalInputValue, setInternalInputValue] = useState("");

  const handleInputChange = useCallback(
    (newValue: string, actionMeta: InputActionMeta) => {
      if (actionMeta.action !== "set-value") {
        setInternalInputValue(newValue);
      }
      onInputChange?.(newValue, actionMeta);
    },
    [onInputChange]
  );

  const handleChange = useCallback(
    (newValue: OnChangeValue<Option, IsMulti>, actionMeta: ActionMeta<Option>) => {
      onChange?.(newValue, actionMeta);
      if (actionMeta.action === "create-option") {
        setInternalInputValue("");
      }
      selectRef.current?.inputRef?.select();
    },
    [onChange, selectRef]
  );

  const handleCreateOption = useCallback(
    (newOptionInput: string) => {
      onCreateOption?.(newOptionInput);
      setInternalInputValue("");
    },
    [onCreateOption]
  );

  if (!isMulti) {
    return {inputValue, onInputChange, onChange, onCreateOption};
  }

  return {
    inputValue: inputValue ?? internalInputValue,
    onInputChange: handleInputChange,
    onChange: handleChange,
    onCreateOption: onCreateOption ? handleCreateOption : undefined,
  };
}
